/**
 * DEMO-ONLY: Market List Store
 *
 * sessionStorage-backed store for demo market list visits.
 * sessionStorage is cleared automatically when the browser tab is closed.
 * We also register a beforeunload handler so a full page refresh clears the list.
 *
 * Allows the scheduler recourse shelf to inject visits into the Unassigned Visits
 * view without real API calls or a shared React context.
 *
 * The beforeunload cleanup is registered as a module side-effect — the first
 * import (from a demo view) wires it up; non-demo routes never load it.
 */

const STORAGE_KEY = 'demo-market-list-visits';
const EVENT_NAME = 'demo-market-list-updated';

// Register page-refresh cleanup on first import.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    sessionStorage.removeItem(STORAGE_KEY);
  });
}

export interface MarketListVisit {
  id: number;
  patientFirstName: string;
  patientLastName: string;
  startTime: string; // ISO
  endTime: string;
  arrivalWindowStart: string;
  arrivalWindowEnd: string;
  visitTypeName: string;
}

export function getMarketListVisits(): MarketListVisit[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addMarketListVisit(visit: MarketListVisit): void {
  const visits = getMarketListVisits();
  if (!visits.find((v) => v.id === visit.id)) {
    visits.push(visit);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function removeMarketListVisit(id: number): void {
  const visits = getMarketListVisits().filter((v) => v.id !== id);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function clearMarketListVisits(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function subscribeToMarketList(handler: () => void): () => void {
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
